import type { DataSeries, RenderTooltipContentData } from '@shopify/polaris-viz';
import type { IRMetricKey } from '../../../../common/type';

import { useFilterByCampaignParams } from '~/presentation/reports/query/hooks';
import { IncreasePercentage, getIncrease } from '~/modules/analytics';
import { isReverseReportMetric } from '../../../../common/type';
import { InlineStack, Text } from '@shopify/polaris';
import { CHART_COLORS } from '../../../constants';
import { WrapperTooltip } from './WrapperTooltip';
import { formatPercent } from '../../../utils';
import { useCallback, useMemo } from 'react';

export interface VersionTooltipProps {
  data: RenderTooltipContentData;
  seriesKey?: Set<string>;
  formatValue: (value: number) => string;
  seriesData?: DataSeries[];
  metricKey?: IRMetricKey;
}

export const VersionTooltip: React.FC<VersionTooltipProps> = ({
  data,
  seriesKey,
  formatValue,
  seriesData,
  metricKey,
}) => {
  const { activeIndex } = data;

  const { hasFilterByCampaign } = useFilterByCampaignParams();

  // Support bar chart because it doesn't provide trend data
  const getTrendDataPoint = useCallback(
    (activeIndex: number, name?: string) => {
      if (!seriesData) return undefined;

      const series = seriesData?.find((series) => series.name === name);
      return series?.data[activeIndex];
    },
    [seriesData],
  );

  const variantData = useMemo(() => {
    const variants = data?.dataSeries?.filter((series) => seriesKey?.has(`${series.name}`)) || [];

    return variants.map((series) => {
      const name = series.name;
      const seriesData = series.data;
      const currentData = getTrendDataPoint(activeIndex, name) ?? seriesData?.[activeIndex];
      const previousData = seriesData?.[activeIndex - 1];
      const value = Number(currentData.value || 0);
      const previousValue = Number(previousData?.value || 0);
      const increase = hasFilterByCampaign
        ? getIncrease(value, previousValue)
        : formatPercent(currentData);

      return {
        name,
        value: formatValue(value),
        increase,
        color: `${series.color}` || CHART_COLORS[0],
      };
    });
  }, [data, activeIndex, seriesKey, formatValue, hasFilterByCampaign, getTrendDataPoint]);

  const title = data.formatters?.titleFormatter?.(data.title || '') || data.title;

  const getContent = () => {
    return variantData.map((variant) => (
      <VersionInfo
        key={variant.name}
        value={variant.value}
        name={variant.name}
        increase={variant.increase}
        color={variant.color}
        metricKey={metricKey}
      />
    ));
  };

  return <WrapperTooltip title={title}>{getContent()}</WrapperTooltip>;
};
interface VersionInfoProps {
  value: string;
  increase: number | string;
  color: string;
  name?: string;
  metricKey?: IRMetricKey;
}

const VersionInfo = ({ value, increase, name, color, metricKey }: VersionInfoProps) => {
  return (
    <InlineStack gap={'200'} align="space-between" blockAlign="center">
      <InlineStack gap={'100'} blockAlign="center">
        <div
          className="w-[8px] h-[8px] rounded-full"
          style={{
            backgroundColor: color,
          }}
        />
        <Text as="p" variant="bodyXs" tone="subdued">
          {name}
        </Text>
      </InlineStack>
      <InlineStack blockAlign="center" gap="100">
        <Text as="span" variant="bodySm" fontWeight="semibold">
          {value}
        </Text>
        <IncreasePercentage increase={increase} isReverse={isReverseReportMetric(metricKey)} />
      </InlineStack>
    </InlineStack>
  );
};
